import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showSuccess, showError } from "redux/actions/ui";

import { PostPowerCarts } from "api/rpc/2024-04/facilityAdmin/facility/powercart/powercart";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import Tabs from "components/tabs/Tabs";
import { StatusCode } from "api/protocols";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface INewInventoryState {
  multipleCarts: boolean;
  cartsAmount: number;
  cartName: string;
  cartNamePrefix: string;
  type: string;
  brand: string;
  starting_number: number;
}

const minimumCartQuantity = 1;

export default function NewCartInventory() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { authStore } = useAppSelector(store => store);
  const { Option } = Select;

  const [newInventoryState, setNewInventoryState] = useState<INewInventoryState>({
    multipleCarts: false,
    cartsAmount: minimumCartQuantity,
    cartName: "",
    cartNamePrefix: "",
    type: "",
    brand: "",
    starting_number: 0,
  });

  const tabs = [
    {
      id: "one-cart",
      content: t("1 Cart"),
      panelID: "one-cart",
    },

    {
      id: "multiple-carts",
      content: t("Multiple Carts"),
      panelID: "multiple-carts",
    },
  ];

  // Handle tab change
  const [selected, setSelected] = useState(0);
  const handleTabChange = (selectedTabIndex: number) => {
    setNewInventoryState(prev => ({ ...prev, cartName: "" }));
    setSelected(selectedTabIndex);
  };

  function handleDropDownChange(value: any, property: string) {
    setNewInventoryState(prevState => ({ ...prevState, [property]: value }));
  }

  function handleInputChange(event: any) {
    const { id, type } = event.target;
    let { value } = event.target;

    if (value < 0 && type === "number") {
      value = 0;
    }

    setNewInventoryState(prevState => ({ ...prevState, [id]: value }));
  }

  async function addToFleet() {
    const powerCartRes = await PostPowerCarts(
      {
        amount: selected === 0 ? 1 : newInventoryState.cartsAmount,
        name: newInventoryState.cartName,
        brand: newInventoryState.brand,
        type: newInventoryState.type,
        starting_number: newInventoryState.starting_number,
      },
      true,
    );

    if (powerCartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating carts")));
      return;
    }

    dispatch(showSuccess(t("Successfully created carts")));

    if (authStore?.user?.permissions?.tee_sheet_starter) {
      history.push("/admin/starter-teesheet-cart-inventory");
    } else {
      history.push("/admin/settings/cart-inventory");
    }
  }

  const primaryAction = {
    content: (
      <>
        <FontAwesomeIcon icon={["far", "plus"]} className="mr-2" />
        {t("Add to Fleet")}
      </>
    ),
    action: addToFleet,
  };

  return (
    <Page
      title={t("New Cart Inventory")}
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to cart inventory"),
          url: authStore?.user?.permissions?.tee_sheet_starter
            ? "/admin/starter-teesheet-cart-inventory"
            : "/admin/settings/cart-inventory",
        },
      ]}
    >
      <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}></Tabs>
      <Card>
        <Card.Section title={t("Inventory Settings")}>
          <FormLayout>
            <FormLayout.Group>
              {selected === 0 ? (
                <Input
                  value={newInventoryState.cartName ?? ""}
                  label={t("Cart Name")}
                  id="cartName"
                  onChange={handleInputChange}
                />
              ) : (
                <div className="flex justify-between gap-2">
                  <Input
                    value={newInventoryState.cartsAmount ?? ""}
                    label={t("Amount of Carts")}
                    id="cartsAmount"
                    onChange={handleInputChange}
                    type="number"
                    className="mr-2"
                  />
                  <Input
                    value={newInventoryState.starting_number ?? ""}
                    label={t("Starting Number")}
                    id="starting_number"
                    onChange={handleInputChange}
                    type="number"
                    min={0}
                  />
                </div>
              )}
              <Select
                defaultValue={"gas"}
                label={t("Type")}
                onChange={(value: any) => handleDropDownChange(value, "type")}
              >
                <Option key={0} name={"gas"} value={"gas"}>
                  <span>{t("Gas")}</span>
                </Option>
                <Option key={1} name={"electric"} value={"electric"}>
                  <span>{t("Electric")}</span>
                </Option>
              </Select>
              <Input value={newInventoryState.brand ?? ""} label={t("Brand")} id="brand" onChange={handleInputChange} />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
